import { useMemo } from "react";
import { SettingsCategory } from "../constants/settingsStructure";
import { useDebounce } from "@/react/hooks/useDebounce";
import {
  getAllSubcategories,
  getSearchableText,
  type SubcategoryWithCategory,
} from "../utils/settingsSearchUtils";

export interface SearchResult extends SubcategoryWithCategory {
  searchableText: string;
}

export type { SubcategoryWithCategory };

export const useSettingsSearch = (
  searchQuery: string,
  categories: SettingsCategory[]
) => {
  const debouncedQuery = useDebounce(searchQuery.trim().toLowerCase(), 300);

  const allSubcategories = useMemo(() => {
    return getAllSubcategories(categories).map((item) => ({
      ...item,
      searchableText: getSearchableText(item.subcategory, item.category),
    }));
  }, [categories]);

  const searchResults = useMemo(() => {
    if (!debouncedQuery) {
      return [];
    }

    const nonDuplicatesSettingIds = new Set<string>();
    return allSubcategories.filter((item) => {
      if (!item.searchableText.includes(debouncedQuery)) return false;

      const key = item.subcategory.settingId ?? item.subcategory.id;
      return !nonDuplicatesSettingIds.has(key) && (nonDuplicatesSettingIds.add(key), true);
    });
  }, [debouncedQuery, allSubcategories]);

  const isSearching = searchQuery.trim().length > 0;

  return {
    searchResults,
    isSearching,
    resultCount: searchResults.length,
  };
};
